import { isDefined, VAT_RATE_VISIBLE_FOOTNOTE_CONTENT, VAT_RATE_VISIBLE_FOOTNOTE_ID } from '@hyundai/shared-lib';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import type { Footnote } from '../../api/api-types';
import { actions } from './footnotes.actions';

interface FootnotesState {
    footnotes: Footnote[];
    activeFootnotes: Record<string, number>;
    activeFootnotesOrder: Set<string>;
}

const initialState: FootnotesState = {
    footnotes: [
        {
            id: 'dealer_legal_notice',
            content:
                'Der Vertragshändler ist für folgende Inhalte verantwortlich: Über uns, Fahrzeugbestand, händlerindividuelle Preise, händlerindividuelle Aktionsseiten, händlereigene Inhalte auf der Serviceseite, händlereigene Inhalte auf der Startseite.<br><br>Hyundai Motor Deutschland ist für folgende Inhalte verantwortlich: Modellseiten, Hyundai Promise, Hyundai Gewerbekundenseite, zentrale Inhalte auf der Serviceseite, zentrale Aktionsseiten, zentrale Inhalte auf der Startseite.',
        },
        {
            id: VAT_RATE_VISIBLE_FOOTNOTE_ID,
            content: VAT_RATE_VISIBLE_FOOTNOTE_CONTENT,
        },
    ],
    activeFootnotes: {},
    activeFootnotesOrder: new Set(),
};

export const state = createFeature({
    name: 'footnotes',
    reducer: createReducer(
        initialState,
        on(actions.addFootnotes, (state, { footnotes, removeWithIdPrefix }): FootnotesState => {
            const stateFootnotes = removeWithIdPrefix
                ? state.footnotes.filter((f) => !f.id.startsWith(removeWithIdPrefix))
                : state.footnotes;
            return {
                ...state,
                footnotes: [
                    ...stateFootnotes,
                    ...footnotes.filter((f) => !stateFootnotes.some((f2) => f2.id === f.id)),
                ],
            };
        }),
        on(
            actions.registerActiveFootnotes,
            (state, { footnotes }): FootnotesState => ({
                ...state,
                activeFootnotes: {
                    ...state.activeFootnotes,
                    ...Object.fromEntries(footnotes.map((f) => [f, (state.activeFootnotes[f] ?? 0) + 1])),
                },
                activeFootnotesOrder: new Set([
                    ...state.activeFootnotesOrder,
                    ...footnotes.filter((f) => !state.activeFootnotesOrder.has(f)),
                ]),
            }),
        ),
        on(actions.unregisterActiveFootnotes, (state, { footnotes }): FootnotesState => {
            const newActiveFootnotes = { ...state.activeFootnotes };
            const newActiveFootnotesOrder = new Set(state.activeFootnotesOrder);
            for (const f of footnotes) {
                if (newActiveFootnotes[f] > 1) {
                    newActiveFootnotes[f]--;
                } else {
                    delete newActiveFootnotes[f];
                    newActiveFootnotesOrder.delete(f);
                }
            }
            return {
                ...state,
                activeFootnotes: newActiveFootnotes,
                activeFootnotesOrder: newActiveFootnotesOrder,
            };
        }),
    ),
    extraSelectors: (baseSelectors) => {
        const selectNumberedFootnotes = createSelector(
            baseSelectors.selectFootnotes,
            baseSelectors.selectActiveFootnotesOrder,
            (footnotes, footnotesOrder) => {
                let i = 1;
                const mapppedFootnotes = [...footnotesOrder]
                    .map((id) => {
                        const footnote = footnotes.find((f2) => f2.id === id);
                        if (!footnote) {
                            return null;
                        }
                        if (!footnote.fixedSign) {
                            while (footnotes.some((f2) => f2.fixedSign === `${i}`)) {
                                i++;
                            }
                        }
                        return {
                            id,
                            content: footnote.content,
                            sign: `${footnote.fixedSign ?? i++}`,
                        };
                    })
                    .filter(isDefined);

                const signFootnotes = mapppedFootnotes
                    .filter((f) => Number.isNaN(Number(f.sign)))
                    .toSorted((a, b) => a.sign.localeCompare(b.sign));
                const numberFootnotes = mapppedFootnotes.filter((f) => !Number.isNaN(Number(f.sign)));
                return [...signFootnotes, ...numberFootnotes];
            },
        );

        return {
            selectNumberedFootnotes,
        };
    },
});
